import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Play,
  Pause,
  Shuffle,
  Copy,
  X,
  Eye,
  Bell,
  BellOff,
  Minus,
  Gauge,
  Timer,
} from "lucide-react";
import ArticleCard from "../components/ArticleCard";
import { articlesAPI, communitiesAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";
import { useReading } from "../context/ReadingContext";
import useStudyTimerExtension from "../hooks/useStudyTimerExtension";
import Spinner from "../components/Spinner";
import {
  getRememberedTutorialLessonId,
  extractPublicLessonId,
  getPublicLessonPath,
  getTutorialLessonPath,
} from "../constants/tutorialContent";
import "../pages/StudyNotes.css";

const PublicNotes = () => {
  const { lessonId, communityId } = useParams();
  const navigate = useNavigate();
  const isCommunityView = Boolean(communityId);
  const rememberedTutorialLessonId = getRememberedTutorialLessonId();
  const canonicalLessonId = extractPublicLessonId(lessonId);
  const backPath = isCommunityView
    ? `/community/${communityId}/lesson/${lessonId}`
    : canonicalLessonId === rememberedTutorialLessonId
      ? getTutorialLessonPath()
      : getPublicLessonPath(lessonId);
  const [notes, setNotes] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(false);
  const [jumpIndex, setJumpIndex] = useState("");
  const [readingModeEnabled, setReadingModeEnabled] = useState(false);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showDurationInfo, setShowDurationInfo] = useState(false);
  const [targetHours, setTargetHours] = useState("");
  const [targetMinutes, setTargetMinutes] = useState("");
  const [showTimerMenu, setShowTimerMenu] = useState(false);
  const [timerHours, setTimerHours] = useState("");
  const [timerMinutes, setTimerMinutes] = useState("");
  const [timerEndTime, setTimerEndTime] = useState(null);
  const [timerDisplay, setTimerDisplay] = useState("");
  useStudyTimerExtension(setTimerEndTime, setTimerDisplay);
  const [chimeEnabled, setChimeEnabled] = useState(() => {
    const saved = localStorage.getItem("chimeEnabled");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [progressLineEnabled, setProgressLineEnabled] = useState(() => {
    const saved = localStorage.getItem("progressLineEnabled");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const lastChimeTimeRef = React.useRef(0);
  const cursorTimeoutRef = React.useRef(null);
  const { addToast } = useToasts();
  const {
    readingModeEnabled: globalReadingMode,
    toggleReadingMode,
    speedMultiplier,
    setSpeedMultiplier,
  } = useReading();

  React.useEffect(() => {
    setReadingModeEnabled(globalReadingMode);
  }, [globalReadingMode]);

  useEffect(() => {
    loadNotes();
  }, [communityId, isCommunityView, lessonId]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      const response = isCommunityView
        ? await communitiesAPI.getLessonItems(communityId, lessonId, "note")
        : await articlesAPI.getPublicNotes(lessonId);
      const rawNotes = isCommunityView ? response.data.items : response.data;
      setNotes(
        rawNotes.map((note) => ({
          title: note.title || "Untitled Note",
          body: note.body || "",
          ...note,
        })),
      );
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const handlePrevious = React.useCallback(() => {
    if (notes.length === 0) return;
    const newIndex = currentIndex > 0 ? currentIndex - 1 : notes.length - 1;
    if (newIndex === currentIndex) {
      setResetTrigger((prev) => !prev);
    } else {
      setCurrentIndex(newIndex);
    }
    setIsPaused(false);
  }, [currentIndex, notes.length]);

  const handleNext = React.useCallback(
    (playChime = true) => {
      if (notes.length === 0) return;

      if (chimeEnabled && playChime) {
        const currentTime = Date.now();
        const timeSinceLastChime = currentTime - lastChimeTimeRef.current;

        if (timeSinceLastChime >= 1000) {
          lastChimeTimeRef.current = currentTime;
          const audioContext = new (
            window.AudioContext || window.webkitAudioContext
          )();
          const now = audioContext.currentTime;
          [523.25].forEach((freq) => {
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            oscillator.frequency.value = freq;
            oscillator.type = "sine";
            gainNode.gain.setValueAtTime(0, now);
            gainNode.gain.linearRampToValueAtTime(0.08, now + 0.02);
            gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.8);
            oscillator.start(now);
            oscillator.stop(now + 0.8);
          });
        }
      }

      const newIndex = currentIndex < notes.length - 1 ? currentIndex + 1 : 0;
      if (newIndex === currentIndex) {
        setResetTrigger((prev) => !prev);
      } else {
        setCurrentIndex(newIndex);
      }
      setIsPaused(false);
    },
    [currentIndex, notes.length, chimeEnabled],
  );

  const togglePause = React.useCallback(() => {
    setIsPaused((prev) => !prev);
  }, []);

  const handleRandom = React.useCallback(() => {
    if (notes.length === 0) return;
    const randomIndex = Math.floor(Math.random() * notes.length);
    setCurrentIndex(randomIndex);
    setIsPaused(false);
    addToast(
      `Random note #${randomIndex + 1} / ${notes.length}`,
      "success-info",
    );
  }, [notes.length, addToast]);

  const handleCopyBody = React.useCallback(() => {
    if (!notes[currentIndex]?.body) return;
    const text = notes[currentIndex].body;
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    addToast("Copied", "success-info");
  }, [notes, currentIndex, addToast]);

  const calculateTotalDuration = () => {
    const remainingNotes = notes.slice(currentIndex);
    const totalWords = remainingNotes.reduce((sum, note) => {
      return sum + (note.body?.split(" ").length || 0);
    }, 0);

    let totalSeconds = 0;
    remainingNotes.forEach((note) => {
      const wordsCount = note.body?.split(" ").length || 0;
      const noteTime = Math.max(
        5,
        Math.ceil((wordsCount / 200) * 60 * speedMultiplier),
      );
      totalSeconds += noteTime;
    });

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const displayTime =
      hours > 0
        ? `${hours}h${minutes}`
        : minutes > 0
          ? `${minutes}m`
          : `${seconds}s`;
    return { hours, minutes, totalSeconds, totalWords, displayTime };
  };

  const calculateSpeedForTargetTime = () => {
    const remainingNotes = notes.slice(currentIndex);
    const totalWords = remainingNotes.reduce(
      (sum, note) => sum + (note.body?.split(" ").length || 0),
      0,
    );
    const targetSeconds =
      (parseInt(targetHours) || 0) * 3600 + (parseInt(targetMinutes) || 0) * 60;
    if (targetSeconds === 0) return;

    const timeAt1x = Math.max(
      5 * remainingNotes.length,
      Math.ceil((totalWords / 200) * 60 * 1),
    );
    const timeAt50x = Math.max(
      5 * remainingNotes.length,
      Math.ceil((totalWords / 200) * 60 * 50),
    );

    let calculatedSpeed;
    let toastMessage;

    if (targetSeconds <= timeAt1x) {
      calculatedSpeed = 1;
      toastMessage = "Set to min speed: ";
    } else if (targetSeconds >= timeAt50x) {
      calculatedSpeed = 50;
      toastMessage = "Set to max speed: ";
    } else {
      calculatedSpeed = Math.round(targetSeconds / ((totalWords / 200) * 60));
      toastMessage = "Speed adjusted to ";
    }

    setSpeedMultiplier(calculatedSpeed);
    setShowSpeedMenu(false);
    addToast(`${toastMessage}${calculatedSpeed}x`, "success-info");
    setTargetHours("");
    setTargetMinutes("");
  };

  const { displayTime } = calculateTotalDuration();

  useEffect(() => {
    if (!timerEndTime) {
      setTimerDisplay("");
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      const remaining = timerEndTime - now;

      if (remaining <= 0) {
        setTimerDisplay("0s");
        return;
      }

      const hours = Math.floor(remaining / 3600000);
      const minutes = Math.floor((remaining % 3600000) / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);

      if (hours > 0) {
        setTimerDisplay(`${hours}h${minutes}`);
      } else if (minutes > 0) {
        setTimerDisplay(`${minutes}m${seconds}`);
      } else {
        setTimerDisplay(`${seconds}s`);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [timerEndTime]);

  useEffect(() => {
    const savedTimer = localStorage.getItem("studyTimer");
    if (!savedTimer) return;

    const parsedEndTime = parseInt(savedTimer, 10);
    if (!Number.isFinite(parsedEndTime)) {
      localStorage.removeItem("studyTimer");
      localStorage.removeItem("studyTimerInfo");
      return;
    }

    if (parsedEndTime > Date.now()) {
      setTimerEndTime(parsedEndTime);
    } else {
      localStorage.removeItem("studyTimer");
      localStorage.removeItem("studyTimerInfo");
    }
  }, []);

  const setTimer = () => {
    const hours = parseInt(timerHours) || 0;
    const minutes = parseInt(timerMinutes) || 0;
    if (hours === 0 && minutes === 0) return;

    const setAt = Date.now();
    const durationMinutes = hours * 60 + minutes;
    const endTime = setAt + hours * 3600000 + minutes * 60000;
    setTimerEndTime(endTime);
    localStorage.setItem("studyTimer", endTime.toString());
    localStorage.setItem(
      "studyTimerInfo",
      JSON.stringify({ setAt, durationMinutes })
    );
    setShowTimerMenu(false);
    setTimerHours("");
    setTimerMinutes("");

    let timeStr;
    if (hours > 0 && minutes > 0) {
      timeStr = `${hours} ${hours === 1 ? "hour" : "hours"} ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    } else if (hours > 0) {
      timeStr = `${hours} ${hours === 1 ? "hour" : "hours"}`;
    } else {
      timeStr = `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
    }
    addToast(`Timer set for ${timeStr}`, "success-info");
  };

  const clearTimer = () => {
    setTimerEndTime(null);
    localStorage.removeItem("studyTimer");
    localStorage.removeItem("studyTimerInfo");
    setTimerDisplay("");
    addToast("Timer cleared", "success-info");
  };

  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === "Escape") {
        if (globalReadingMode) {
          e.stopPropagation();
          e.preventDefault();
          toggleReadingMode();
          if (document.fullscreenElement) {
            document.exitFullscreen();
          }
          addToast("Reading mode OFF", "success-info");
          return;
        }
        navigate(backPath);
      }
    };

    window.addEventListener("keydown", handleEscKey, true);
    return () => window.removeEventListener("keydown", handleEscKey, true);
  }, [backPath, globalReadingMode, toggleReadingMode, addToast, navigate]);

  const prevReadingMode = React.useRef(globalReadingMode);
  React.useEffect(() => {
    if (globalReadingMode) {
      document.body.classList.add("reading-mode");
    } else {
      document.body.classList.remove("reading-mode");

      if (prevReadingMode.current === true) {
        document.body.classList.add("reading-mode-exit");
        setTimeout(() => {
          document.body.classList.remove("reading-mode-exit");
        }, 1000);
      }
    }

    prevReadingMode.current = globalReadingMode;

    return () => {
      document.body.classList.remove("reading-mode");
      document.body.classList.remove("reading-mode-exit");
    };
  }, [globalReadingMode]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (
        document.activeElement.tagName === "INPUT" ||
        document.activeElement.tagName === "TEXTAREA"
      ) {
        return;
      }

      if (e.key === "ArrowLeft") {
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        handleNext(false);
      } else if (e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        togglePause();
      } else if (
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "PageUp" ||
        e.key === "PageDown"
      ) {
        const articleText = document.querySelector(".articlecard-content p");
        if (!articleText) return;

        const scrollAmount = 100;
        const pageScrollAmount = articleText.clientHeight * 0.8;

        switch (e.key) {
          case "ArrowUp":
            e.preventDefault();
            articleText.scrollTop -= scrollAmount;
            break;
          case "ArrowDown":
            e.preventDefault();
            articleText.scrollTop += scrollAmount;
            break;
          case "PageUp":
            e.preventDefault();
            articleText.scrollTop -= pageScrollAmount;
            break;
          case "PageDown":
            e.preventDefault();
            articleText.scrollTop += pageScrollAmount;
            break;
        }
      }
    };

    const handleWheel = (e) => {
      const articleText = document.querySelector(".articlecard-content p");
      if (!articleText) return;

      const articleContent = document.querySelector(".articlecard-content");
      if (!articleContent) return;

      const rect = articleContent.getBoundingClientRect();
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      if (
        mouseX >= rect.left &&
        mouseX <= rect.right &&
        mouseY >= rect.top &&
        mouseY <= rect.bottom
      ) {
        e.preventDefault();
        articleText.scrollTop += e.deltaY;
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
      window.removeEventListener("wheel", handleWheel);
    };
  }, [handlePrevious, handleNext, togglePause]);

  // Hide cursor after 10 seconds of inactivity
  useEffect(() => {
    const handleMouseMove = () => {
      document.body.classList.remove("hide-cursor");
      if (cursorTimeoutRef.current) {
        clearTimeout(cursorTimeoutRef.current);
      }
      cursorTimeoutRef.current = setTimeout(() => {
        document.body.classList.add("hide-cursor");
      }, 10000);
    };

    window.addEventListener("mousemove", handleMouseMove);
    handleMouseMove();

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.body.classList.remove("hide-cursor");
      if (cursorTimeoutRef.current) {
        clearTimeout(cursorTimeoutRef.current);
      }
    };
  }, []);

  if (loading) return <Spinner visible={true} />;

  if (notes.length === 0) {
    return (
      <div className="public-notes-empty">
        <h2>No notes available</h2>
        <button
          className="public-notes-empty-button"
          onClick={() => navigate(backPath)}
        >
          {isCommunityView ? "Back to Community Lesson" : "Back to Home"}
        </button>
      </div>
    );
  }

  return (
    <div className="public-notes-wrapper">
      <button
        className="public-notes-home-exit-button"
        onClick={() => {
          navigate(backPath);
        }}
        title={isCommunityView ? "Back to Community Lesson" : "Back to Home"}
      >
        <X size={18} strokeWidth={2} />
      </button>

      <main className="studynotes-main public-notes-main">
        <div className="studynotes-middle">
          <ArticleCard
            article={notes[currentIndex]}
            currentIndex={currentIndex}
            totalArticles={notes.length}
            onPrevious={handlePrevious}
            onNext={handleNext}
            isPaused={isPaused}
            onResetTimer={() => setIsPaused(false)}
            resetTrigger={resetTrigger}
            onTogglePause={togglePause}
            onJumpToIndex={(index) => {
              if (index === currentIndex) {
                setResetTrigger((prev) => !prev);
              } else {
                setCurrentIndex(index);
                addToast(`${index + 1} / ${notes.length}`, "success-info");
              }
              setIsPaused(false);
            }}
            onVisibilityChange={() => {}}
            folders={[]}
            selectedFolder={null}
            onFolderChange={() => {}}
            isEditMode={false}
            onArticleChange={() => {}}
            speedMultiplier={speedMultiplier}
            progressLineEnabled={progressLineEnabled}
          />

          {notes.length > 0 && (
            <div
              className={`studynotes-buttons-row ${globalReadingMode ? "reading-mode-fade" : ""}`}
              style={{ position: "relative" }}
            >
              <div className="studynotes-button-group">
                <button
                  className="studynotes-random-button"
                  onClick={() => {
                    toggleReadingMode();
                    if (!readingModeEnabled) {
                      document.documentElement.requestFullscreen();
                    } else if (document.fullscreenElement) {
                      document.exitFullscreen();
                    }
                    addToast(
                      `Reading mode ${globalReadingMode ? "OFF" : "ON"}`,
                      "success-info",
                    );
                  }}
                  title="Reading mode"
                >
                  <Eye size={18} />
                </button>
                <button
                  className="studynotes-random-button"
                  onClick={() => {
                    const newValue = !chimeEnabled;
                    setChimeEnabled(newValue);
                    localStorage.setItem(
                      "chimeEnabled",
                      JSON.stringify(newValue),
                    );
                    addToast(
                      `Chime ${chimeEnabled ? "OFF" : "ON"}`,
                      "success-info",
                    );
                  }}
                  title={chimeEnabled ? "Chime ON" : "Chime OFF"}
                >
                  {chimeEnabled ? <Bell size={18} /> : <BellOff size={18} />}
                </button>
                <button
                  className="studynotes-random-button"
                  onClick={() => {
                    const newValue = !progressLineEnabled;
                    setProgressLineEnabled(newValue);
                    localStorage.setItem(
                      "progressLineEnabled",
                      JSON.stringify(newValue),
                    );
                    addToast(
                      `Progress line ${progressLineEnabled ? "OFF" : "ON"}`,
                      "success-info",
                    );
                  }}
                  title={
                    progressLineEnabled ? "Progress line ON" : "Progress line OFF"
                  }
                >
                  <Minus
                    size={18}
                    style={{ opacity: progressLineEnabled ? 1 : 0.3 }}
                  />
                </button>
              </div>
              <div style={{ position: "relative" }}>
                <button
                  className="studynotes-random-button"
                  onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                  title="Number of times to repeat"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "0px",
                    padding: "4px",
                  }}
                >
                  <Gauge size={18} />
                  <span style={{ fontSize: "10px", lineHeight: "1" }}>
                    {speedMultiplier === 5
                      ? 3
                      : speedMultiplier === 15
                        ? 10
                        : speedMultiplier}
                    x
                  </span>
                </button>
                {showSpeedMenu && (
                  <div
                    className="studynotes-popup-menu"
                    style={{
                      position: "absolute",
                      bottom: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      marginBottom: "8px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      zIndex: 1000,
                      alignItems: "center",
                      background: "var(--color-bg-primary)",
                      padding: "6px",
                      borderRadius: "4px",
                    }}
                  >
                    {[1, 3, 10].map((displaySpeed) => {
                      const actualSpeed =
                        displaySpeed === 3
                          ? 5
                          : displaySpeed === 10
                            ? 15
                            : displaySpeed;
                      return (
                        <button
                          key={displaySpeed}
                          onClick={() => {
                            setSpeedMultiplier(actualSpeed);
                            setShowSpeedMenu(false);
                            addToast(`Speed: ${displaySpeed}x`, "success-info");
                          }}
                          style={{
                            padding: "6px 12px",
                            background:
                              speedMultiplier === actualSpeed
                                ? "rgba(220, 220, 220, 0.3)"
                                : "transparent",
                            border: "1px solid var(--color-border)",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "var(--color-text-primary)",
                            transition: "background 0.2s",
                            minWidth: "60px",
                          }}
                        >
                          {displaySpeed}x
                        </button>
                      );
                    })}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                        padding: "6px",
                        background: "var(--color-bg-primary)",
                        borderRadius: "4px",
                        alignItems: "center",
                        width: "fit-content",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "2px",
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="number"
                          placeholder="h"
                          value={targetHours}
                          onChange={(e) => setTargetHours(e.target.value)}
                          onKeyPress={(e) =>
                            e.key === "Enter" && calculateSpeedForTargetTime()
                          }
                          style={{
                            width: "30px",
                            padding: "4px",
                            background: "var(--color-bg-primary)",
                            border: "1px solid var(--color-border)",
                            borderRadius: "4px",
                            color: "var(--color-text-primary)",
                            fontSize: "12px",
                            textAlign: "center",
                          }}
                        />
                        <span
                          style={{
                            color: "var(--color-text-primary)",
                            fontSize: "12px",
                          }}
                        >
                          :
                        </span>
                        <input
                          type="number"
                          placeholder="m"
                          value={targetMinutes}
                          onChange={(e) => setTargetMinutes(e.target.value)}
                          onKeyPress={(e) =>
                            e.key === "Enter" && calculateSpeedForTargetTime()
                          }
                          style={{
                            width: "30px",
                            padding: "4px",
                            background: "var(--color-bg-primary)",
                            border: "1px solid var(--color-border)",
                            borderRadius: "4px",
                            color: "var(--color-text-primary)",
                            fontSize: "12px",
                            textAlign: "center",
                          }}
                        />
                      </div>
                      <button
                        onClick={calculateSpeedForTargetTime}
                        style={{
                          padding: "4px 8px",
                          background: "transparent",
                          border: "1px solid var(--color-border)",
                          borderRadius: "4px",
                          color: "var(--color-text-primary)",
                          fontSize: "12px",
                          cursor: "pointer",
                          width: "100%",
                        }}
                      >
                        Set
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <button
                className="studynotes-random-button"
                onClick={() => {
                  setShowDurationInfo(true);
                  setTimeout(() => setShowDurationInfo(false), 5000);
                }}
                title="Total duration"
                style={{ position: "relative" }}
              >
                <span style={{ fontSize: "11px", fontWeight: "600" }}>
                  {displayTime}
                </span>
                {showDurationInfo && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "100%",
                      left: "0",
                      marginBottom: "12px",
                      background: "var(--color-bg-primary)",
                      border: "1px solid var(--color-border)",
                      padding: "6px",
                      borderRadius: "4px",
                      zIndex: 1000,
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "var(--color-text-primary)",
                      whiteSpace: "nowrap",
                      outline: "none",
                      pointerEvents: "none",
                    }}
                  >
                  <div>
                    {notes.length - currentIndex} notes left at{" "}
                    {speedMultiplier === 5 ? 3 : speedMultiplier === 15 ? 10 : speedMultiplier}x
                  </div>
                  </div>
                )}
              </button>
              <input
                type="number"
                min="1"
                max={notes.length}
                value={jumpIndex}
                onChange={(e) => setJumpIndex(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    const index = parseInt(jumpIndex) || 1;
                    if (index >= 1 && index <= notes.length) {
                      setCurrentIndex(index - 1);
                      setIsPaused(false);
                      addToast(`${index} / ${notes.length}`, "success-info");
                      e.target.blur();
                    }
                  }
                }}
                onFocus={(e) => {
                  e.target.select();
                }}
                onBlur={() => setJumpIndex("")}
                placeholder={currentIndex + 1}
                className="studynotes-control-input"
                title="Click to enter a new index"
              />
              <button
                className="studynotes-random-button"
                onClick={togglePause}
                title={isPaused ? "Resume" : "Pause"}
              >
                {isPaused ? <Play size={18} /> : <Pause size={18} />}
              </button>
              <div className="studynotes-total-display" title="Total notes">
                {notes.length}
              </div>
              <button
                className="studynotes-random-button"
                onClick={handleRandom}
                title="Random Note"
              >
                <Shuffle size={18} />
              </button>
              <div className="studynotes-button-group">
                <div style={{ position: "relative" }}>
                  <button
                    className="studynotes-random-button"
                    onClick={() =>
                      timerEndTime
                        ? clearTimer()
                        : setShowTimerMenu(!showTimerMenu)
                    }
                    title={timerEndTime ? "Clear timer" : "Set timer"}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "0px",
                    padding: "4px",
                  }}
                >
                  {timerEndTime ? (
                    <>
                      <Timer size={14} />
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: "400",
                          lineHeight: "1",
                        }}
                      >
                        {timerDisplay}
                      </span>
                    </>
                  ) : (
                    <Timer size={18} />
                  )}
                </button>
                {showTimerMenu && (
                  <div
                    className="studynotes-popup-menu"
                    style={{
                      position: "absolute",
                      bottom: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      marginBottom: "8px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      zIndex: 1000,
                      background: "var(--color-bg-primary)",
                      padding: "6px",
                      borderRadius: "4px",
                      border: "1px solid var(--color-border)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "2px",
                        alignItems: "center",
                      }}
                    >
                      <input
                        type="number"
                        placeholder="h"
                        value={timerHours}
                        onChange={(e) => setTimerHours(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && setTimer()}
                        style={{
                          width: "30px",
                          padding: "4px",
                          background: "var(--color-bg-primary)",
                          border: "1px solid var(--color-border)",
                          borderRadius: "4px",
                          color: "var(--color-text-primary)",
                          fontSize: "12px",
                          textAlign: "center",
                        }}
                      />
                      <span
                        style={{
                          color: "var(--color-text-primary)",
                          fontSize: "12px",
                        }}
                      >
                        :
                      </span>
                      <input
                        type="number"
                        placeholder="m"
                        value={timerMinutes}
                        onChange={(e) => setTimerMinutes(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && setTimer()}
                        style={{
                          width: "30px",
                          padding: "4px",
                          background: "var(--color-bg-primary)",
                          border: "1px solid var(--color-border)",
                          borderRadius: "4px",
                          color: "var(--color-text-primary)",
                          fontSize: "12px",
                          textAlign: "center",
                        }}
                      />
                    </div>
                    <button
                      onClick={setTimer}
                      style={{
                        padding: "4px 8px",
                        background: "transparent",
                        border: "1px solid var(--color-border)",
                        borderRadius: "4px",
                        color: "var(--color-text-primary)",
                        fontSize: "12px",
                        cursor: "pointer",
                        width: "100%",
                      }}
                    >
                      Set
                    </button>
                  </div>
                )}
                </div>
              </div>
              <button
                className="studynotes-random-button"
                onClick={handleCopyBody}
                title="Copy Text"
              >
                <Copy size={18} />
              </button>
            </div>
          )}
        </div>

        {globalReadingMode && (
          <button
            className="reading-mode-toggle-persistent"
            onClick={() => {
              toggleReadingMode();
              if (document.fullscreenElement) {
                document.exitFullscreen();
              }
              addToast("Reading mode OFF", "success-info");
            }}
            title="Exit reading mode"
          >
            <X size={18} strokeWidth={2} />
          </button>
        )}
      </main>
    </div>
  );
};

export default PublicNotes;
